// Данные раздела «Креативы»: пул, карточка ролика, батчи, генерации (api /api/creatives, файлы — /api/media).
import { voiceGenderOf } from '@autoaff/schemas/voices';
import type { Tone } from '@autoaff/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';

export type CreativeStatus =
  | 'draft'
  | 'rendering'
  | 'qc'
  | 'ready'
  | 'approved'
  | 'rejected'
  | 'uploaded'
  | 'live'
  | 'fatigued'
  | 'archived';

export const STATUS: Record<CreativeStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Черновик', tone: 'default' },
  rendering: { label: 'Собирается', tone: 'info' },
  qc: { label: 'На проверке', tone: 'info' },
  ready: { label: 'Готов', tone: 'accent' },
  approved: { label: 'Отобран в рекламу', tone: 'success' },
  rejected: { label: 'Отклонён', tone: 'danger' },
  uploaded: { label: 'Загружен в FB', tone: 'success' },
  live: { label: 'В рекламе', tone: 'success' },
  fatigued: { label: 'Выгорел', tone: 'warning' },
  archived: { label: 'В архиве', tone: 'default' },
};
export const statusOf = (s: string) => STATUS[s as CreativeStatus] ?? { label: s, tone: 'default' as Tone };

export const QC: Record<string, { label: string; tone: Tone }> = {
  pending: { label: 'Ждёт проверки', tone: 'info' },
  running: { label: 'Проверяется', tone: 'info' },
  passed: { label: 'Проверка пройдена', tone: 'success' },
  warn: { label: 'Есть замечания', tone: 'warning' },
  failed: { label: 'Проверка не пройдена', tone: 'danger' },
};

/** Пометка платформы «выгорает»: CTR ролика падает 2 дня подряд, а цена лида его объявления растёт. */
export interface FatigueMark {
  since: string;
  summary: string;
}

/**
 * Фильтры пула — как в макете: по смыслу для владельца, а не по каждому статусу. «Выгорают» — и выгоревшие
 * по решению агента, и помеченные платформой.
 */
export const FILTERS: Array<{
  key: string;
  label: string;
  match: (s: string, c?: { fatigue?: FatigueMark | null }) => boolean;
}> = [
  { key: 'all', label: 'Все', match: () => true },
  { key: 'live', label: 'В рекламе', match: (s) => s === 'live' || s === 'uploaded' },
  { key: 'ready', label: 'Готовы', match: (s) => s === 'ready' || s === 'approved' },
  { key: 'work', label: 'В работе', match: (s) => s === 'draft' || s === 'rendering' || s === 'qc' },
  { key: 'fatigued', label: 'Выгорают', match: (s, c) => s === 'fatigued' || !!c?.fatigue },
  { key: 'arch', label: 'Архив', match: (s) => s === 'rejected' || s === 'archived' },
];

export const mediaUrl = (rel: string | null | undefined, download?: string) =>
  rel ? `/api/media/${rel}${download ? `?download=1&name=${encodeURIComponent(download)}` : ''}` : null;

export interface CreativeRow {
  id: number;
  offerId: number;
  offerSlug: string;
  offerName: string;
  code: string;
  title: string | null;
  status: string;
  statusNote: string | null;
  language: string | null;
  angle: string | null;
  hook: string | null;
  voice: string | null;
  delivery: string | null;
  durationSec: number | null;
  costUsd: number;
  source: string;
  batchId: number | null;
  /** Когда ролик появился в пуле (у старого api нет — тогда по времени изменения). */
  createdAt?: string;
  updatedAt: string;
  openComments: number;
  /** Платформа заметила выгорание (у старого api поля нет). */
  fatigue?: FatigueMark | null;
  version: { id: number; version: number; qcStatus: string; video: string; poster: string | null } | null;
}

export interface BatchRow {
  id: number;
  offerId: number;
  offerSlug: string;
  offerName: string;
  title: string;
  mode: 'autonomous' | 'revisions';
  status: 'planned' | 'in_progress' | 'review' | 'done' | 'cancelled';
  targetCount: number;
  made: number;
  /** Агент сейчас работает над батчем (или работа ждёт очереди). */
  working: boolean;
  taskId: number | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface CreativesData {
  creatives: CreativeRow[];
  offers: Array<{ id: number; slug: string; name: string; n: number }>;
  batches: BatchRow[];
  counts: Record<string, number>;
}

export function useCreatives() {
  return useQuery({
    queryKey: ['creatives'],
    queryFn: () => api<CreativesData>('/api/creatives'),
    refetchInterval: 30_000,
  });
}

export interface QcCheck {
  id: string;
  label: string;
  status: 'ok' | 'warn' | 'fail' | 'skip';
  detail: string;
}

export interface VersionView {
  id: number;
  version: number;
  video: string;
  poster: string | null;
  sheet: string | null;
  durationSec: number | null;
  qcStatus: string;
  qc: { status: string; checks: QcCheck[] } | null;
  judge: { score: number; issues: string[]; notes: string } | null;
  diff: { items: Array<{ area: string; t?: number; text: string }>; summary: string } | null;
  changeSummary: string | null;
  costUsd: number;
  createdBy: string;
  createdAt: string;
  clips: Array<{ src: string | undefined; dur: number }>;
}

export interface CommentView {
  id: number;
  versionId: number;
  tStart: number;
  tEnd: number | null;
  text: string;
  status: 'open' | 'in_work' | 'done' | 'wontfix';
  reply: string | null;
  author: string;
  resolvedVersionId: number | null;
  createdAt: string;
}

export interface CreativeDetail {
  id: number;
  code: string;
  title: string | null;
  status: string;
  statusNote: string | null;
  language: string | null;
  spec: Record<string, unknown> | null;
  script: string | null;
  scriptRu: string | null;
  adHeadline: string | null;
  adText: string | null;
  costUsd: number;
  source: string;
  offer: { id: number; slug: string; name: string; priceUzs: number | null };
  batch: { id: number; title: string; status: string } | null;
  fatigue?: FatigueMark | null;
  currentVersionId: number | null;
  versions: VersionView[];
  comments: CommentView[];
  usages: Array<{
    role: string;
    kind: string;
    title: string | null;
    origin: string;
    licence: string | null;
    path: string;
  }>;
}

export function useCreative(id: number) {
  return useQuery({
    queryKey: ['creative', id],
    queryFn: () => api<CreativeDetail>(`/api/creatives/${id}`),
    refetchInterval: 20_000,
  });
}

export const tc = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export const DELIVERY: Record<string, string> = {
  ugc: 'от лица покупателя',
  narrator: 'диктор',
  diktor: 'диктор',
};

/**
 * Готовые голоса Gemini по полу — у роликов Creatives Room и батчей до перехода на свои голоса (docs/VOICES.md).
 * Пол голосов пула — из VOICES. В интерфейсе — «женский голос», имя голоса — только в технических деталях.
 */
const FEMALE_VOICES = new Set([
  'Aoede',
  'Laomedeia',
  'Leda',
  'Zephyr',
  'Kore',
  'Callirrhoe',
  'Autonoe',
  'Despina',
  'Erinome',
  'Achernar',
  'Gacrux',
  'Pulcherrima',
  'Vindemiatrix',
  'Sulafat',
]);
const MALE_VOICES = new Set([
  'Orus',
  'Puck',
  'Sadaltager',
  'Algieba',
  'Charon',
  'Fenrir',
  'Enceladus',
  'Iapetus',
  'Umbriel',
  'Algenib',
  'Rasalgethi',
  'Alnilam',
  'Schedar',
  'Achird',
  'Zubenelgenubi',
  'Sadachbia',
]);

/** «женский», «мужской»; неизвестный голос — null. */
export function voiceGender(voice: string | null | undefined, gender?: unknown): string | null {
  const g = typeof gender === 'string' ? gender.toLowerCase() : '';
  if (g.startsWith('f') || g.startsWith('ж')) return 'женский';
  if (g.startsWith('m') || g.startsWith('м')) return 'мужской';
  if (!voice) return null;
  const pool = voiceGenderOf(voice);
  if (pool === 'f' || FEMALE_VOICES.has(voice)) return 'женский';
  if (pool === 'm' || MALE_VOICES.has(voice)) return 'мужской';
  return null;
}

/** Голос и язык ролика для карточки: «женский голос, UZ». */
export function voiceLine(voice: string | null | undefined, lang: string | null | undefined): string | null {
  const g = voiceGender(voice);
  const l = lang?.toUpperCase();
  if (g) return l ? `${g} голос, ${l}` : `${g} голос`;
  return voice ? (l ? `голос, ${l}` : 'голос') : (l ?? null);
}
